import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="promoters--events--mails"
export default class extends Controller {
  static targets = [
    "eventMailForm",
    "staffIds",
    "ticketAll",
    "ticket",
    "selectedTicket",
    "ticketForm",
    "ticketSubmit",
    "testMailForm",
    "testMailSubmit",
    "applicant",
    "applicantForm",
    "applicantSubmit",
    "applicantSearchForm",
    "visitor",
    "visitorForm",
    "visitorSubmit",
    "visitorSearchForm",
    "event",
    "eventForm",
    "eventSearchForm",
    "eventSubmit",
    "selectedUserId",
  ];
  static values = {
    // メール送信対象上限
    limit: { type: Number, default: 10 },
  };

  connect() {
    this.toggleTicketSubmit();
    this.toggleTestMailSubmit();
    this.toggleApplicantSubmit();
    this.toggleVisitorSubmit();
  }

  // 送信対象チケット:すべて をクリックする
  ticketTargetAllChecked() {
    this.ticketAllTarget.click();
  }

  // 全チケット選択をクリア
  clearSelectedTickets() {
    document.getElementById("selected_tickets").textContent = "";
    const checkboxes = Array.from(this.ticketTargets).filter((checkbox) => checkbox.checked);
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.ticketCheck();
  }

  // チケット選択を解除
  releaseTicket(event) {
    const ticketId = event.currentTarget.dataset.ticketId;
    const checkbox = document.getElementById(`event_mail_form_ticket_ids_${ticketId}`);
    document.getElementById(`ticket_${ticketId}`).remove();
    checkbox.checked = false;
    this.ticketCheck();
  }

  // testMailForm のパラメータを eventMailForm にセットする
  setTestMailParams() {
    this.staffIdsTarget.innerHTML = "";
    this.testMailFormTarget.querySelectorAll("input").forEach((input) => {
      if (input.type === "checkbox") {
        if (input.checked) {
          this.addHiddenInput(input.name, input.value);
        }
      }
    });
  }

  addHiddenInput(name, value) {
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value;
    this.staffIdsTarget.appendChild(hiddenInput);
  }

  // drawer_controller.jsのopenを呼び出す
  openDrawer(event) {
    this.externalControllerAction(event, "drawer", "open");
  }

  // modal_controller.jsのshowを呼び出す
  showModal(event) {
    this.externalControllerAction(event, "modal", "show");
  }

  // テストメール送信ボタンの状態を更新
  toggleTestMailSubmit() {
    if (this.hasTestMailSubmitTarget) {
      this.updateSubmitButtonState(this.testMailSubmitTarget, "#sendMail .staff-check");
    }
  }

  // チケットドロワー送信
  ticketDrawerSubmit(event) {
    event.preventDefault();
    event.currentTarget.form.requestSubmit();
    this.checkedTicketDisabled();
  }

  // チェック済みのチェックボックスをDisabledにする
  checkedTicketDisabled() {
    this.ticketTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  // チケット選択時にチェックボックスの状態を更新
  ticketCheck() {
    this.toggleCheckboxDisabled(this.ticketTargets, this.limitValue);
  }

  // チケット一括選択
  ticketCheckAll(event) {
    this.setCheckBoxesState(this.ticketTargets, event.currentTarget.checked, this.limitValue);
  }

  // チケット選択時のsubmitボタンの状態を更新
  toggleTicketSubmit() {
    if (this.hasTicketSubmitTarget) {
      this.updateSubmitButtonState(this.ticketSubmitTarget, "#selectTicket .ticket-check");
    }
  }

  // ユーザー選択を解除
  releaseUser(event) {
    const userId = event.currentTarget.dataset.userId;
    const sendTarget = event.currentTarget.dataset.sendTarget;
    const checkboxes = document.querySelectorAll(`.${sendTarget}_${userId}`);
    document.querySelectorAll(`.selected_user_${userId}`).forEach((element) => {
      element.remove();
    });
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.userCheck(sendTarget);
  }

  userDrawerSubmit(event) {
    const sendTarget = event.currentTarget.dataset.sendTarget;
    event.preventDefault();
    switch (event.currentTarget.dataset.sendTarget) {
      case "applicant":
        this.resetVisitorDrawer();
        break;
      case "visitor":
        this.resetApplicantDrawer();
        break;
      default:
        break;
    }
    if (sendTarget === "applicant") {
      this.applicantFormTarget.requestSubmit(); // applicant用フォームを送信
    } else if (sendTarget === "visitor") {
      this.visitorFormTarget.requestSubmit(); // visitor用フォームを送信
    }
    this.checkedUserDisabled(sendTarget);
  }

  // チェック済みのチェックボックスをDisabledにする
  checkedUserDisabled(sendTarget) {
    const targets = this[`${sendTarget}Targets`];
    targets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  // 送信対象ユーザチェックボックスのクリック時の処理
  userCheckboxClick(event) {
    const checkbox = event.currentTarget;
    const sendTarget = checkbox.dataset.sendTarget;
    if (sendTarget) {
      this.toggleSameUserCheckboxes(checkbox, sendTarget, checkbox.checked);
      this.userCheck(sendTarget);
    }
  }

  // 送信対象ユーザチェックボックスの同一クラスのチェックボックスをトグル
  toggleSameUserCheckboxes(checkbox, sendTarget, isChecked) {
    const pattern = new RegExp(`^${sendTarget}_\\d+$`);
    const checkedClass = Array.from(checkbox.classList).find((className) => pattern.test(className));
    const targets = this[`${sendTarget}Targets`];
    if (checkedClass) {
      targets
        .filter((c) => c.classList.contains(checkedClass))
        .forEach((c) => {
          c.checked = isChecked;
        });
    }
  }

  // 送信対象ユーザチェックボックスのクラス名を取得
  sendTargetUserClasses(sendTarget, isChecked = () => true) {
    const pattern = new RegExp(`^${sendTarget}_\\d+$`);
    const classes = new Set();
    const targets = this[`${sendTarget}Targets`];

    targets.forEach((checkbox) => {
      if (isChecked(checkbox)) {
        const matchedClass = Array.from(checkbox.classList).find((className) => pattern.test(className));
        if (matchedClass) {
          classes.add(matchedClass);
        }
      }
    });

    return Array.from(classes);
  }

  // 送信対象ユーザチェックボックスのチェックされているクラス名を取得
  checkedSendTargetUserClasses(sendTarget) {
    return this.sendTargetUserClasses(sendTarget, (checkbox) => checkbox.checked);
  }

  // 送信対象ユーザチェックボックスのチェックされていないクラス名を取得
  uncheckedSendTargetUserClasses(sendTarget) {
    return this.sendTargetUserClasses(sendTarget, (checkbox) => !checkbox.checked);
  }

  // ユーザ選択時にチェックボックスの状態を更新
  userCheck(sendTarget) {
    const targets = this[`${sendTarget}Targets`];
    this.toggleUserCheckboxDisabled(targets, sendTarget, this.limitValue);
  }

  // ユーザ一括選択
  userCheckAll(event) {
    this.setSendTargetUserCheckBoxesState(
      event.currentTarget.dataset.sendTarget,
      event.currentTarget.checked,
      this.limitValue,
    );
  }

  // 応募者選択時のsubmitボタンの状態を更新
  toggleApplicantSubmit() {
    if (this.hasApplicantSubmitTarget) {
      this.updateSubmitButtonState(this.applicantSubmitTarget, "#selectApplicant .applicant-check");
    }
  }

  // 応募者ドロワー送信
  applicantDrawerSubmit(event) {
    event.preventDefault();
    event.currentTarget.form.requestSubmit();
    this.checkedApplicantDisabled();
  }

  toggleDisabledSelectedUser(event) {
    const applicant_disabled = event.currentTarget.dataset.sendTarget != "applicant";
    const visitor_disabled = event.currentTarget.dataset.sendTarget != "visitor";
    const applicantHidden = document.getElementById("selected_applicant").querySelectorAll('input[type="hidden"]');
    const visitorHidden = document.getElementById("selected_visitor").querySelectorAll('input[type="hidden"]');
    applicantHidden.forEach((hiddenInput) => {
      hiddenInput.disabled = applicant_disabled;
    });
    visitorHidden.forEach((hiddenInput) => {
      hiddenInput.disabled = visitor_disabled;
    });
  }

  resetApplicantDrawer() {
    document.querySelectorAll(".applicant-check").forEach((checkbox) => {
      checkbox.disabled = false;
    });
    this.applicantSearchFormTarget.reset();
    this.applicantFormTarget.reset();
  }

  resetVisitorDrawer() {
    document.querySelectorAll(".visitor-check").forEach((checkbox) => {
      checkbox.disabled = false;
    });
    this.visitorSearchFormTarget.reset();
    this.visitorFormTarget.reset();
  }

  // チェック済みのチェックボックスをDisabledにする
  checkedApplicantDisabled() {
    this.applicantTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  // 参加者選択時のsubmitボタンの状態を更新
  toggleVisitorSubmit() {
    if (this.hasVisitorSubmitTarget) {
      this.updateSubmitButtonState(this.visitorSubmitTarget, "#selectVisitor .visitor-check");
    }
  }

  // イベントドロワー送信
  eventDrawerSubmit(event) {
    event.preventDefault();
    this.eventFormTarget.requestSubmit();
    this.checkedEventDisabled();
  }

  resetEventDrawer() {
    document.querySelectorAll(".event-check").forEach((checkbox) => {
      checkbox.disabled = false;
    });
    this.eventSearchFormTarget.reset();
    this.eventFormTarget.reset();
  }

  // チェック済みのチェックボックスをDisabledにする
  checkedEventDisabled() {
    this.eventTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  // イベント選択時にチェックボックスの状態を更新
  eventCheck() {
    this.toggleCheckboxDisabled(this.eventTargets);
  }

  // イベント一括選択
  eventCheckAll(event) {
    this.setCheckBoxesState(this.eventTargets, event.currentTarget.checked);
  }

  // イベント選択時のsubmitボタンの状態を更新
  toggleEventSubmit() {
    if (this.hasEventSubmitTarget) {
      this.updateSubmitButtonState(this.eventSubmitTarget, "#selectEvent .event-check");
    }
  }

  // 全イベント選択をクリア
  clearSelectedEvents() {
    document.getElementById("selected_events").textContent = "";
    this.resetEventDrawer();
  }

  // イベント選択を解除
  releaseEvent(event) {
    const eventId = event.currentTarget.dataset.eventId;
    const checkboxes = document.querySelectorAll(`.event_${eventId}`);
    document.querySelectorAll(`.selected_event_${eventId}`).forEach((element) => {
      element.remove();
    });
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.eventCheck();
  }

  // チェックボックスの状態を設定
  setCheckBoxesState(targets, isChecked, limit = null) {
    targets.forEach((checkbox) => {
      if (checkbox.disabled) return;
      checkbox.checked = isChecked;
      this.toggleCheckboxDisabled(targets, limit);
    });
  }

  // 送信対象ユーザチェックボックスの状態を設定
  setSendTargetUserCheckBoxesState(sendTarget, isChecked, limit = null) {
    const targets = this[`${sendTarget}Targets`];
    targets.forEach((checkbox) => {
      if (checkbox.disabled) return;
      this.toggleSameUserCheckboxes(checkbox, sendTarget, isChecked);
      this.toggleUserCheckboxDisabled(targets, sendTarget, limit);
    });
  }

  // submitボタンの状態を更新
  updateSubmitButtonState(button, selector) {
    if (button) {
      const checkedCount = document.querySelectorAll(selector + ":checked").length;
      button.setAttribute("aria-disabled", checkedCount === 0 ? "true" : "false");
    }
  }

  // テストメール送信
  testMailSubmit(event) {
    event.preventDefault();
    const form = this.eventMailFormTarget;
    const originalFormAction = form.action;
    this.setTestMailParams();
    form.action = event.currentTarget.form.action;
    form.requestSubmit();
    this.staffIdsTarget.innerHTML = "";
    form.action = originalFormAction;
  }

  // trクリック時にチェックボックスをクリックする
  trClick(event) {
    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]');
    if (checkbox && event.target !== checkbox) {
      checkbox.click();
    }
  }

  toggleCheckboxDisabled(targets, limit = null) {
    const checkedCount = Array.from(targets).filter((checkbox) => checkbox.checked).length;
    const unchecked = Array.from(targets).filter((checkbox) => !checkbox.checked);
    unchecked.forEach((checkbox) => {
      if (limit === null || checkedCount < limit) {
        checkbox.disabled = false;
      } else {
        checkbox.checked = false;
        checkbox.disabled = true;
      }
    });
  }

  toggleUserCheckboxDisabled(targets, sendTarget, limit = null) {
    const checkedCount = this.checkedSendTargetUserClasses(sendTarget).length;
    const uncheckedClasses = this.uncheckedSendTargetUserClasses(sendTarget);
    const unchecked = Array.from(targets).filter((checkbox) =>
      uncheckedClasses.some((uncheckedClass) => checkbox.classList.contains(uncheckedClass)),
    );
    unchecked.forEach((checkbox) => {
      if (limit === null || checkedCount < limit) {
        checkbox.disabled = false;
      } else {
        checkbox.checked = false;
        checkbox.disabled = true;
      }
    });
  }

  // 外部コントローラーのアクションを呼び出す
  externalControllerAction(event, identifier, action) {
    const targetId = event.currentTarget.dataset.targetId;
    const controller = this.application.getControllerForElementAndIdentifier(
      document.getElementById(targetId),
      identifier,
    );
    controller[action]();
  }
}
